import React, { useState } from "react";
import { Modal, Platform, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from "react-native";

// Range values: "YYYY-MM" (month precision, default) or "YYYY-MM-DD" (day precision)
export interface DateRange {
  start: string;
  end: string;
}

interface Props {
  label: string;
  value: DateRange | null;
  onChange: (range: DateRange | null) => void;
  onOpen?: () => void;
  precision?: "month" | "day";
}

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const CELL_W = 44;
const CELL_H = 44;
const MONTH_W = CELL_W * 7;

function offsetMonths(year: number, month: number, n: number) {
  const d = new Date(year, month - 1 + n, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function daysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate();
}

function firstWeekDay(year: number, month: number) {
  return new Date(year, month - 1, 1).getDay(); // 0 = Sun
}

function dk(year: number, month: number, day: number) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatDisplay(start: string, end: string) {
  if (start.length === 10 || end.length === 10) {
    const f = (s: string) => `${s.slice(8, 10)} ${MONTH_NAMES[parseInt(s.slice(5, 7), 10) - 1]} ${s.slice(0, 4)}`;
    if (start === end) return f(start);
    return `${f(start)} – ${f(end)}`;
  }
  const [sy, sm] = start.split("-");
  const [ey, em] = end.split("-");
  if (start === end) return `${MONTH_NAMES[parseInt(sm, 10) - 1]} ${sy}`;
  return `${MONTH_NAMES[parseInt(sm, 10) - 1]} ${sy} – ${MONTH_NAMES[parseInt(em, 10) - 1]} ${ey}`;
}

const DateRangeFilter: React.FC<Props> = ({ label, value, onChange, onOpen, precision = "month" }) => {
  const { width: winW } = useWindowDimensions();
  const dual = Platform.OS === "web" ? winW >= 720 : false;

  const [visible, setVisible] = useState(false);
  const [selStart, setSelStart] = useState<string | null>(null);
  const [selEnd, setSelEnd] = useState<string | null>(null);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth() + 1);

  const openModal = () => {
    onOpen?.();
    if (value) {
      setSelStart(value.start.length === 10 ? value.start : `${value.start}-01`);
      setSelEnd(value.end.length === 10 ? value.end : `${value.end}-01`);
      const { year, month } = offsetMonths(
        parseInt(value.end.slice(0, 4), 10),
        parseInt(value.end.slice(5, 7), 10),
        dual ? -1 : 0
      );
      setViewYear(year);
      setViewMonth(month);
    } else {
      setSelStart(null);
      setSelEnd(null);
      const now = new Date();
      setViewYear(now.getFullYear());
      setViewMonth(now.getMonth() + 1);
    }
    setVisible(true);
  };

  const prev = () => {
    const o = offsetMonths(viewYear, viewMonth, -1);
    setViewYear(o.year);
    setViewMonth(o.month);
  };

  const next = () => {
    const o = offsetMonths(viewYear, viewMonth, 1);
    setViewYear(o.year);
    setViewMonth(o.month);
  };

  const onDayPress = (key: string) => {
    if (!selStart || (selStart && selEnd)) {
      setSelStart(key);
      setSelEnd(null);
    } else {
      if (key < selStart) {
        setSelEnd(selStart);
        setSelStart(key);
      } else if (key === selStart) {
        setSelEnd(key);
      } else {
        setSelEnd(key);
      }
    }
  };

  type DayState = "start" | "end" | "in-range" | "single" | "none";
  const getDayState = (key: string): DayState => {
    if (!selStart) return "none";
    const end = selEnd ?? selStart;
    if (key === selStart && key === end) return "single";
    if (key === selStart) return "start";
    if (key === end) return "end";
    if (key > selStart && key < end) return "in-range";
    return "none";
  };

  // Whether left/right neighbors in the same row are in-range (for the continuous band)
  const hasRangeLeft = (year: number, month: number, day: number, colIdx: number): boolean => {
    if (colIdx === 0) return false;
    const prev = dk(year, month, day - 1);
    const s = getDayState(prev);
    return s === "in-range" || s === "start" || s === "end" || s === "single";
  };

  const hasRangeRight = (year: number, month: number, day: number, colIdx: number, totalDays: number): boolean => {
    if (colIdx === 6) return false;
    if (day >= totalDays) return false;
    const next = dk(year, month, day + 1);
    const s = getDayState(next);
    return s === "in-range" || s === "start" || s === "end" || s === "single";
  };

  const renderMonth = (year: number, month: number) => {
    const firstDay = firstWeekDay(year, month);
    const numDays = daysInMonth(year, month);
    const cells: (number | null)[] = Array(firstDay).fill(null);
    for (let d = 1; d <= numDays; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    const weeks: (number | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    return (
      <View style={{ width: MONTH_W }}>
        {/* Month title */}
        <Text style={styles.monthTitle}>
          {year}-{String(month).padStart(2, "0")}
        </Text>
        {/* Weekday header */}
        <View style={styles.weekRow}>
          {WEEK_DAYS.map((d) => (
            <View key={d} style={styles.dayCell}>
              <Text style={styles.weekDayLabel}>{d}</Text>
            </View>
          ))}
        </View>
        {/* Day rows */}
        {weeks.map((week, wi) => (
          <View key={wi} style={styles.weekRow}>
            {week.map((day, di) => {
              if (!day) return <View key={di} style={styles.dayCell} />;
              const key = dk(year, month, day);
              const state = getDayState(key);
              const isSelected = state === "start" || state === "end" || state === "single";
              const inRange = state === "in-range";
              const bandLeft = inRange && hasRangeLeft(year, month, day, di);
              const bandRight = inRange && hasRangeRight(year, month, day, di, numDays);
              const startConnectsRight = state === "start" && hasRangeRight(year, month, day, di, numDays);
              const endConnectsLeft = state === "end" && hasRangeLeft(year, month, day, di);

              return (
                <TouchableOpacity
                  key={di}
                  style={styles.dayCell}
                  onPress={() => onDayPress(key)}
                  activeOpacity={0.7}
                >
                  {/* Band layer for in-range cells */}
                  {inRange && (
                    <View style={[
                      styles.bandLayer,
                      !bandLeft && styles.bandLayerLeftCap,
                      !bandRight && styles.bandLayerRightCap,
                    ]} />
                  )}
                  {/* Half-band extending right from start cell */}
                  {startConnectsRight && (
                    <View style={styles.halfBandRight} />
                  )}
                  {/* Half-band extending left from end cell */}
                  {endConnectsLeft && (
                    <View style={styles.halfBandLeft} />
                  )}
                  {/* Circle for selected days */}
                  <View style={[
                    styles.dayInner,
                    isSelected && state === "start" && styles.dayInnerStart,
                    isSelected && state === "end" && styles.dayInnerEnd,
                    isSelected && state === "single" && styles.dayInnerStart,
                  ]}>
                    <Text style={[
                      styles.dayText,
                      isSelected && styles.dayTextSelected,
                      inRange && styles.dayTextInRange,
                    ]}>
                      {day}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        ))}
      </View>
    );
  };

  const selLabel = () => {
    if (!selStart) return "Select start date";
    const start = selStart;
    if (!selEnd) return `${start} — select end date`;
    return `${start} – ${selEnd}`;
  };

  const right = dual ? offsetMonths(viewYear, viewMonth, 1) : null;

  const apply = () => {
    if (selStart) {
      const end = selEnd ?? selStart;
      onChange(
        precision === "day"
          ? { start: selStart, end }
          : { start: selStart.slice(0, 7), end: end.slice(0, 7) }
      );
    }
    setVisible(false);
  };

  const clear = () => {
    onChange(null);
    setVisible(false);
  };

  const modalW = dual ? MONTH_W * 2 + 80 + 40 : MONTH_W + 80;

  return (
    <View style={styles.wrapper}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity style={styles.field} onPress={openModal}>
        <Text style={styles.fieldText}>
          {value ? formatDisplay(value.start, value.end) : "All"}
        </Text>
      </TouchableOpacity>

      <Modal visible={visible} transparent animationType="fade" onRequestClose={() => setVisible(false)}>
        <View style={styles.overlay}>
          <View style={[styles.modalCard, { width: modalW }]}>

            {/* Header */}
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Date</Text>
              <TouchableOpacity onPress={() => setVisible(false)} style={styles.closeBtn}>
                <Text style={styles.closeBtnText}>✕</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.divider} />

            {/* Calendars */}
            <View style={styles.calendarRow}>
              <TouchableOpacity onPress={prev} style={styles.navBtn}>
                <Text style={styles.navBtnText}>‹</Text>
              </TouchableOpacity>

              <View style={styles.monthsContainer}>
                {renderMonth(viewYear, viewMonth)}
                {dual && right && (
                  <>
                    <View style={styles.monthDivider} />
                    {renderMonth(right.year, right.month)}
                  </>
                )}
              </View>

              <TouchableOpacity onPress={next} style={styles.navBtn}>
                <Text style={styles.navBtnText}>›</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.divider} />

            {/* Footer */}
            <View style={styles.footer}>
              <Text style={styles.selLabel}>{selLabel()}</Text>
              <View style={styles.footerBtns}>
                <TouchableOpacity onPress={clear} style={styles.clearBtn}>
                  <Text style={styles.clearBtnText}>Clear</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={apply}
                  disabled={!selStart}
                  style={[styles.applyBtn, !selStart && styles.applyBtnDisabled]}
                >
                  <Text style={styles.applyBtnText}>Apply</Text>
                </TouchableOpacity>
              </View>
            </View>

          </View>
        </View>
      </Modal>
    </View>
  );
};

const RANGE_COLOR = "#FCE4EC";
const START_COLOR = "#C62828";
const END_COLOR = "#6A1B9A";

const styles = StyleSheet.create({
  wrapper: { minWidth: 160, flex: 1 },
  label: { fontSize: 12, color: "#555", marginBottom: 4, fontWeight: "500" },
  field: {
    borderWidth: 1, borderColor: "#ddd", borderRadius: 6, minHeight: 38,
    paddingHorizontal: 10, justifyContent: "center", backgroundColor: "#fff",
  },
  fieldText: { fontSize: 13, color: "#333" },

  overlay: { flex: 1, backgroundColor: "rgba(0,0,0,0.3)", justifyContent: "center", alignItems: "center" },
  modalCard: {
    backgroundColor: "#fff", borderRadius: 12, overflow: "hidden",
    shadowColor: "#000", shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18, shadowRadius: 24, elevation: 12,
  },
  modalHeader: {
    flexDirection: "row", justifyContent: "space-between", alignItems: "center",
    paddingHorizontal: 24, paddingVertical: 16,
  },
  modalTitle: { fontSize: 18, fontWeight: "700", color: "#111" },
  closeBtn: { padding: 6 },
  closeBtnText: { fontSize: 18, color: "#888", lineHeight: 20 },
  divider: { height: 1, backgroundColor: "#F0F0F0" },

  calendarRow: {
    flexDirection: "row", alignItems: "flex-start",
    paddingHorizontal: 12, paddingVertical: 20,
  },
  navBtn: {
    width: 32, alignItems: "center", justifyContent: "center",
    paddingTop: CELL_H + 8, // align with the first day row
  },
  navBtnText: { fontSize: 28, color: "#555", lineHeight: 32 },
  monthsContainer: { flexDirection: "row", flex: 1, justifyContent: "center" },
  monthDivider: { width: 1, backgroundColor: "#F0F0F0", marginHorizontal: 16 },

  monthTitle: {
    fontSize: 17, fontWeight: "700", color: "#111",
    textAlign: "center", marginBottom: 8, height: CELL_H, lineHeight: CELL_H,
  },

  weekRow: { flexDirection: "row" },
  dayCell: {
    width: CELL_W, height: CELL_H,
    justifyContent: "center", alignItems: "center",
  },
  weekDayLabel: { fontSize: 12, color: "#9CA3AF", fontWeight: "500" },

  // Absolute band layer sitting behind the day circle
  bandLayer: {
    position: "absolute",
    top: 4, bottom: 4, left: 0, right: 0,
    backgroundColor: RANGE_COLOR,
  },
  bandLayerLeftCap: { borderTopLeftRadius: CELL_H / 2, borderBottomLeftRadius: CELL_H / 2 },
  bandLayerRightCap: { borderTopRightRadius: CELL_H / 2, borderBottomRightRadius: CELL_H / 2 },
  // Half-band on right half of start cell
  halfBandRight: {
    position: "absolute",
    top: 4, bottom: 4,
    left: CELL_W / 2, right: 0,
    backgroundColor: RANGE_COLOR,
  },
  // Half-band on left half of end cell
  halfBandLeft: {
    position: "absolute",
    top: 4, bottom: 4,
    left: 0, right: CELL_W / 2,
    backgroundColor: RANGE_COLOR,
  },

  dayInner: {
    width: CELL_W - 4, height: CELL_W - 4,
    borderRadius: (CELL_W - 4) / 2,
    justifyContent: "center", alignItems: "center",
  },
  dayInnerStart: { backgroundColor: START_COLOR },
  dayInnerEnd: { backgroundColor: END_COLOR },

  dayText: { fontSize: 14, color: "#111", fontWeight: "400" },
  dayTextSelected: { color: "#fff", fontWeight: "700" },
  dayTextInRange: { color: "#111" },

  footer: {
    flexDirection: "row", justifyContent: "space-between", alignItems: "center",
    paddingHorizontal: 24, paddingVertical: 14,
  },
  selLabel: { fontSize: 12, color: "#555", flex: 1, marginRight: 12 },
  footerBtns: { flexDirection: "row", gap: 8 },
  clearBtn: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 6, backgroundColor: "#F3F4F6" },
  clearBtnText: { fontSize: 13, color: "#555", fontWeight: "600" },
  applyBtn: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 6, backgroundColor: "#1976d2" },
  applyBtnDisabled: { backgroundColor: "#90CAF9" },
  applyBtnText: { fontSize: 13, color: "#fff", fontWeight: "600" },
});

export default DateRangeFilter;
